import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Section } from "@/components/Section";
import {
  RefreshCcw,
  Building2,
  Mail,
  Globe,
  CheckCircle2,
  Clock,
  Inbox,
  HelpCircle,
  Rocket,
  Sparkles,
  Download,
  AlertTriangle,
  FileText,
  ChevronRight,
  X,
  ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE = [
  { id: "submitted", label: "Submitted", icon: Inbox, tone: "bg-blue-50 text-blue-700 border-blue-100" },
  { id: "needs_clarification", label: "Needs Clarification", icon: HelpCircle, tone: "bg-amber-50 text-amber-700 border-amber-100" },
  { id: "approved", label: "Approved", icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  { id: "live", label: "Live", icon: Rocket, tone: "bg-violet-50 text-violet-700 border-violet-100" },
];

const CLARIFICATION_REASONS = [
  { id: "missing_insurance", label: "Missing insurance" },
  { id: "missing_w9", label: "Missing W-9" },
  { id: "incomplete_service_description", label: "Incomplete service description" },
  { id: "unclear_workflow", label: "Unclear workflow" },
  { id: "missing_licensing_documentation", label: "Missing licensing documentation" },
  { id: "unsupported_claims", label: "Unsupported claims" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
};

const categoryLabel = (id) =>
  ({
    diagnostics_labs: "Diagnostics / Labs",
    wellness_services: "Wellness Services",
    remote_monitoring: "Remote Monitoring",
    digital_health: "Digital Health",
    fulfillment_logistics: "Fulfillment / Logistics",
    pharmacy: "Pharmacy",
    other: "Other",
  }[id] || id);

export default function AdminVendors() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [clarifyFor, setClarifyFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/vendor-applications");
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast.error("Failed to load applications.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const grouped = useMemo(() => {
    const g = { submitted: [], needs_clarification: [], approved: [], live: [] };
    for (const it of items) {
      const s = PIPELINE.some((x) => x.id === it.status) ? it.status : "submitted";
      g[s].push(it);
    }
    return g;
  }, [items]);

  const selected = items.find((i) => i.id === selectedId) || null;

  const refresh = async (id) => {
    try {
      const r = await api.get(`/admin/vendor-applications/${id}`);
      setItems((prev) => prev.map((it) => (it.id === id ? r.data : it)));
    } catch {
      // ignore
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/vendor-applications/${id}/approve`, { note: null });
      toast.success("Vendor approved — added to network.");
      refresh(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    }
  };

  const requestClarification = async (id, reasons, note) => {
    try {
      await api.post(`/admin/vendor-applications/${id}/request-clarification`, { reasons, note });
      toast.success("Clarification requested — vendor will be notified.");
      setClarifyFor(null);
      refresh(id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not request clarification.");
    }
  };

  const updateDeployment = async (id, patch) => {
    try {
      const r = await api.post(`/admin/vendor-applications/${id}/deploy`, patch);
      setItems((prev) => prev.map((it) => (it.id === id ? r.data : it)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deployment update failed.");
    }
  };

  const runSmartMatch = async (id) => {
    try {
      await api.post(`/admin/vendor-applications/${id}/smart-match`, {});
      toast.success("Running smart match…");
      // Poll for the matches; LLM call is ~10–18s.
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        try {
          const r = await api.get(`/admin/vendor-applications/${id}`);
          if (r.data?.clinic_matches?.generated_at) {
            setItems((prev) => prev.map((it) => (it.id === id ? r.data : it)));
            clearInterval(poll);
            const n = (r.data.clinic_matches.items || []).length;
            toast.success(n > 0 ? `Found ${n} match${n > 1 ? "es" : ""}.` : "No strong matches found.");
          }
        } catch {
          // ignore
        }
        if (attempts > 12) clearInterval(poll);
      }, 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Smart match failed.");
    }
  };

  const regenerateAi = async (id) => {
    try {
      await api.post(`/admin/vendor-applications/${id}/regenerate-ai`, {});
      toast.success("AI summary regenerating…");
      setTimeout(() => refresh(id), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate.");
    }
  };

  return (
    <div data-testid="admin-vendors-page">
      <Section className="pt-28 pb-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-3">
              Internal · Vendor Pipeline
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 leading-[1.05]">
              Vendor pipeline
            </h1>
            <p className="mt-3 text-slate-600 max-w-xl">
              Submitted → Needs Clarification → Approved → Live. Lightweight operational review. AI summary
              assists; admin decides.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-vendors-refresh-btn"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            <RefreshCcw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {PIPELINE.map((s) => (
            <div
              key={s.id}
              data-testid={`admin-vendors-stat-${s.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3"
            >
              <span className={`w-9 h-9 rounded-lg border flex items-center justify-center ${s.tone}`}>
                <s.icon className="w-4 h-4" />
              </span>
              <div>
                <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-400">
                  {s.label}
                </div>
                <div className="font-display text-xl font-bold text-slate-900 tabular-nums">
                  {grouped[s.id].length}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pt-0">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <p className="text-slate-500">No vendor applications yet.</p>
          </div>
        )}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {PIPELINE.map((s) => (
              <Column
                key={s.id}
                stage={s}
                items={grouped[s.id]}
                onSelect={setSelectedId}
                selectedId={selectedId}
              />
            ))}
          </div>
        )}
        <div className="mt-10 text-center">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-900">← Back to site</Link>
        </div>
      </Section>

      {selected && (
        <DetailDrawer
          application={selected}
          onClose={() => setSelectedId(null)}
          onApprove={() => approve(selected.id)}
          onRequestClarification={() => setClarifyFor(selected.id)}
          onUpdateDeployment={(patch) => updateDeployment(selected.id, patch)}
          onRegenerateAi={() => regenerateAi(selected.id)}
          onRunSmartMatch={() => runSmartMatch(selected.id)}
        />
      )}

      {clarifyFor && (
        <ClarificationModal
          onSubmit={(reasons, note) => requestClarification(clarifyFor, reasons, note)}
          onClose={() => setClarifyFor(null)}
        />
      )}
    </div>
  );
}

function Column({ stage, items, onSelect, selectedId }) {
  const Icon = stage.icon;
  return (
    <div className="rounded-2xl bg-slate-50/60 border border-slate-200 p-3 flex flex-col">
      <div className="flex items-center justify-between px-2 py-2">
        <div className="flex items-center gap-2">
          <span className={`w-7 h-7 rounded-md border flex items-center justify-center ${stage.tone}`}>
            <Icon className="w-3.5 h-3.5" />
          </span>
          <div className="font-display font-semibold text-slate-900">{stage.label}</div>
        </div>
        <span className="text-xs font-semibold text-slate-400 tabular-nums">{items.length}</span>
      </div>
      <div className="mt-1 space-y-2 min-h-[60px]" data-testid={`admin-vendors-col-${stage.id}`}>
        {items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-400">
            No applications
          </div>
        ) : (
          items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => onSelect(it.id)}
              data-testid={`admin-vendors-card-${it.id}`}
              className={`group w-full text-left rounded-xl bg-white border px-3.5 py-3 hover:shadow-sm transition-all ${
                selectedId === it.id ? "border-slate-900 shadow-sm" : "border-slate-200"
              }`}
            >
              <div className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-slate-700" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-slate-900 text-sm truncate">
                    {it.company?.legal_name || "Unnamed vendor"}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {categoryLabel(it.company?.vendor_category)}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10.5px] text-slate-400">
                    <Clock className="w-2.5 h-2.5" />
                    {fmtDate(it.submitted_at || it.created_at)}
                    <span className="ml-auto text-blue-700 font-bold">{it.completion_pct}%</span>
                  </div>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-slate-500 mt-1" />
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function DetailDrawer({
  application,
  onClose,
  onApprove,
  onRequestClarification,
  onUpdateDeployment,
  onRegenerateAi,
  onRunSmartMatch,
}) {
  const a = application;
  const c = a.company || {};
  const ai = a.ai_summary;
  const docs = a.documents || {};
  const dep = a.deployment || {};
  const services = a.services || [];
  const matches = a.clinic_matches || null;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div data-testid="admin-vendors-detail-drawer" className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <aside className="relative ml-auto w-full max-w-2xl h-full bg-white border-l border-slate-200 overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-600 truncate">
              {categoryLabel(c.vendor_category)} · {a.status?.replaceAll("_", " ")}
            </div>
            <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5 truncate">
              {c.legal_name || "Unnamed vendor"}
            </h2>
            <div className="mt-1 text-xs text-slate-500">
              {c.primary_contact || "—"} · submitted {fmtDate(a.submitted_at || a.created_at)} · {a.completion_pct}% complete
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="admin-vendors-close-btn" className="text-slate-400 hover:text-slate-900 rounded-full w-8 h-8 border border-slate-200 flex items-center justify-center" aria-label="Close">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Beta / minimal application indicator — shown when the vendor
              only submitted Company info (services, docs, ops all empty).
              Signals to the admin that a full-profile clarification link
              should be sent when we're ready to onboard. */}
          {services.length === 0 &&
            !docs?.w9 && !docs?.coi && !docs?.license &&
            !(a.operational || {}).onboarding_process &&
            !(a.operational || {}).support_process &&
            !(a.operational || {}).invoicing_process && (
            <div
              data-testid="admin-vendors-beta-minimal-banner"
              className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
            >
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-800 mb-1">
                Beta · Company Info only
              </div>
              <p className="text-sm text-amber-900 leading-relaxed">
                This applicant submitted the short beta form (company info only).
                When ready to onboard, use <strong>Request Clarification</strong> to email
                them a link for services, documents (W-9, COI, licenses), and
                operational answers.
              </p>
            </div>
          )}

          {/* Snapshot */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2.5 text-sm">
            <RowItem icon={Mail} label="Support email" value={
              <a className="text-blue-700 hover:underline" href={`mailto:${c.support_email}`}>{c.support_email || "—"}</a>
            } />
            <RowItem icon={Globe} label="Website" value={c.website || "—"} />
            <RowItem icon={Building2} label="HQ" value={c.headquarters_location || "—"} />
            <RowItem icon={Building2} label="States serviced" value={(a.operational || {}).states_serviced || "—"} />
            <RowItem icon={CheckCircle2} label="Recurring capable" value={(a.operational || {}).recurring_fulfillment_capable ? "Yes" : "No"} />
            <RowItem icon={CheckCircle2} label="Wholesale / contracted-rate billing" value={(services.find((s) => s.client_bill_compatible) ? "Yes (at least one service)" : "No")} />
          </div>

          {/* Services + products summary */}
          <div data-testid="admin-vendors-services">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
              Services rendered
            </div>
            {services.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No services submitted.</div>
            ) : (
              <div className="space-y-2">
                {services.map((s, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-3">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-bold text-sm text-slate-900">{s.name || `Service #${i + 1}`}</span>
                      <span className="text-[11px] text-slate-500">{s.fulfillment_timeline || "—"}</span>
                    </div>
                    {s.description ? (<p className="mt-1 text-xs text-slate-600 leading-relaxed">{s.description}</p>) : null}
                    {s.products && s.products.filter((p) => p.name).length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {s.products.filter((p) => p.name).map((p, j) => (
                          <span key={j} className="text-[10.5px] uppercase tracking-[0.12em] font-bold rounded-full bg-blue-50 text-blue-700 border border-blue-100 px-2 py-0.5">
                            {p.name}
                          </span>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Documents checklist */}
          <div data-testid="admin-vendors-doc-checklist">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Documents</div>
            <ul className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
              {[
                { id: "w9", label: "W-9 uploaded" },
                { id: "coi", label: "Insurance uploaded" },
                { id: "license", label: "Licenses uploaded" },
              ].map((d) => {
                const present = !!docs[d.id];
                return (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
                    <div className="flex items-center gap-2">
                      {present ? (
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <span className="w-4 h-4 rounded-full border border-slate-300" />
                      )}
                      <span className={present ? "text-slate-900" : "text-slate-500"}>{d.label}</span>
                      {present && docs[d.id]?.filename ? (
                        <span className="text-[11px] text-slate-400 truncate">· {docs[d.id].filename}</span>
                      ) : null}
                    </div>
                    {present ? (
                      <a
                        href={`${BACKEND_URL}/api/admin/vendor-applications/${a.id}/document/${d.id}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`admin-vendors-doc-${d.id}-download`}
                        className="text-xs font-semibold text-blue-700 hover:text-blue-900 inline-flex items-center gap-1"
                      >
                        <Download className="w-3 h-3" /> Download
                      </a>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* AI summary */}
          <div data-testid="admin-vendors-ai-summary">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> AI operational summary
              </div>
              <button
                type="button"
                onClick={onRegenerateAi}
                data-testid="admin-vendors-regen-ai-btn"
                className="text-[11px] font-semibold text-blue-700 hover:text-blue-900"
              >
                Regenerate
              </button>
            </div>
            {!ai ? (
              <div className="rounded-xl border border-dashed border-slate-200 p-4 text-xs text-slate-500 italic">
                AI summary not yet generated. (May take ~10 seconds after submission.)
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3 text-sm">
                <p className="text-slate-800 leading-relaxed">
                  <span className="text-[10.5px] uppercase tracking-[0.16em] font-bold text-blue-700 block mb-1">
                    Operational Readiness
                  </span>
                  {ai.operational_summary}
                </p>
                <BulletBlock title="Missing Information" tone="amber" items={ai.missing_information} />
                <BulletBlock title="Workflow Notes" tone="slate" items={ai.workflow_notes} />
                <BulletBlock title="Risk Flags" tone="rose" items={ai.risk_flags} icon={AlertTriangle} />
                <p className="text-[10.5px] text-slate-400">
                  {ai.model} · generated {fmtDate(ai.generated_at)}
                </p>
              </div>
            )}
          </div>

          {/* Deployment toggles (if approved or live) */}
          {(a.status === "approved" || a.status === "live") && (
            <SmartMatchPanel
              matches={matches}
              currentTargets={dep.target_clinic_ids || []}
              deployedToAll={dep.deployed_to_all_clinics !== false}
              onRun={onRunSmartMatch}
              onDeployToTargets={(ids) =>
                onUpdateDeployment({
                  target_clinic_ids: ids,
                  deployed_to_all_clinics: ids.length === 0,
                })
              }
            />
          )}
          {(a.status === "approved" || a.status === "live") && (
            <div data-testid="admin-vendors-deployment">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">
                Deployment settings
              </div>
              <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                <DeployRow label="Visible in clinic portal" checked={!!dep.visible_in_clinic_portal} onChange={(v) => onUpdateDeployment({ visible_in_clinic_portal: v })} testid="admin-vendors-toggle-visible" />
                <DeployRow label="Accepting orders" checked={!!dep.accepting_orders} onChange={(v) => onUpdateDeployment({ accepting_orders: v })} testid="admin-vendors-toggle-orders" />
                <DeployRow label="Recurring workflows enabled" checked={!!dep.recurring_workflows_enabled} onChange={(v) => onUpdateDeployment({ recurring_workflows_enabled: v })} testid="admin-vendors-toggle-recurring" />
                <DeployRow label="Deploy to all clinics" checked={dep.deployed_to_all_clinics !== false} onChange={(v) => onUpdateDeployment({ deployed_to_all_clinics: v })} testid="admin-vendors-toggle-all-clinics" />
              </div>
              <p className="mt-2 text-[11px] text-slate-500">
                Vendor goes <b>Live</b> when both "Visible in clinic portal" and "Accepting orders" are enabled.
              </p>
            </div>
          )}

          {/* Actions */}
          {(a.status === "submitted" || a.status === "needs_clarification") && (
            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={onApprove}
                data-testid="admin-vendors-approve-btn"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" /> Approve vendor
              </button>
              <button
                type="button"
                onClick={onRequestClarification}
                data-testid="admin-vendors-request-clarification-btn"
                className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
              >
                <HelpCircle className="w-4 h-4" /> Request clarification
              </button>
            </div>
          )}

          {/* Disclaimer */}
          <div className="pt-2 flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Vendors remain independently responsible for compliance. Nodera approval is operational
              network approval only — Nodera does not provide legal or regulatory certification. Clinics
              remain independently responsible for clinical decision-making.
            </span>
          </div>
        </div>
      </aside>
    </div>
  );
}

function RowItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-7 h-7 rounded-md bg-white border border-slate-200 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-slate-400">{label}</div>
        <div className="text-sm text-slate-800 break-words">{value || "—"}</div>
      </div>
    </div>
  );
}

function BulletBlock({ title, items, tone, icon: Icon }) {
  if (!items || items.length === 0) return null;
  const toneCls = {
    amber: "text-amber-700",
    rose: "text-rose-700",
    slate: "text-slate-600",
  }[tone] || "text-slate-600";
  return (
    <div>
      <div className={`text-[10.5px] uppercase tracking-[0.16em] font-bold ${toneCls} mb-1 flex items-center gap-1`}>
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {title}
      </div>
      <ul className="space-y-1 text-sm text-slate-700">
        {items.map((m, i) => (<li key={i} className="leading-relaxed">• {m}</li>))}
      </ul>
    </div>
  );
}

function SmartMatchPanel({ matches, currentTargets, deployedToAll, onRun, onDeployToTargets }) {
  const items = matches?.items || [];
  const generated = matches?.generated_at;
  const [selected, setSelected] = useState(new Set(currentTargets || []));

  useEffect(() => {
    setSelected(new Set(currentTargets || []));
  }, [currentTargets]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const dirty = useMemo(() => {
    const a = Array.from(selected).sort().join(",");
    const b = Array.from(currentTargets || []).sort().join(",");
    return a !== b;
  }, [selected, currentTargets]);

  return (
    <div data-testid="admin-vendors-smart-match">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Smart clinic matches
        </div>
        <button
          type="button"
          onClick={onRun}
          data-testid="admin-vendors-run-smart-match-btn"
          className="text-[11px] font-semibold text-blue-700 hover:text-blue-900"
        >
          {generated ? "Re-run match" : "Run smart match"}
        </button>
      </div>

      {!generated ? (
        <div className="rounded-xl border border-dashed border-slate-200 p-4 text-xs text-slate-500 italic">
          AI matches not yet generated. Click "Run smart match" to score the top clinics most likely
          to use this vendor based on category + program fit.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-xs text-slate-500">
          No strong matches found in the current clinic network. The vendor will still be visible to all
          clinics if "Deploy to all clinics" remains enabled.
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {items.map((m) => {
              const checked = selected.has(m.clinic_id);
              return (
                <li
                  key={m.clinic_id}
                  data-testid={`admin-vendors-match-${m.clinic_id}`}
                  className={`rounded-xl border p-3 transition-colors ${
                    checked ? "border-blue-300 bg-blue-50/40" : "border-slate-200 bg-white"
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(m.clinic_id)}
                      data-testid={`admin-vendors-match-toggle-${m.clinic_id}`}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-bold text-slate-900 truncate">
                          {m.clinic_name}
                        </span>
                        <span className="text-[11px] font-bold text-blue-700 tabular-nums shrink-0">
                          {m.score}/100
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-600 leading-relaxed">{m.reasoning}</p>
                    </div>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex items-center justify-between gap-3">
            <span className="text-[10.5px] text-slate-400">
              Pool: {matches?.clinic_pool_size || 0} clinics · {matches?.model || ""}
            </span>
            <button
              type="button"
              onClick={() => onDeployToTargets(Array.from(selected))}
              disabled={!dirty}
              data-testid="admin-vendors-deploy-to-matches-btn"
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {selected.size === 0
                ? "Reset to all clinics"
                : `Deploy to ${selected.size} selected clinic${selected.size > 1 ? "s" : ""}`}
            </button>
          </div>
          {deployedToAll && selected.size === 0 ? (
            <p className="mt-1 text-[10.5px] text-slate-400">
              Currently deployed to <b>all clinics</b>. Select matches to scope deployment.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

function DeployRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
      <span className="text-slate-700">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testid}
        onClick={() => onChange(!checked)}
        className={`relative w-9 h-5 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${checked ? "translate-x-4" : "translate-x-0"}`} />
      </button>
    </div>
  );
}

function ClarificationModal({ onSubmit, onClose }) {
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState("");
  const toggle = (id) => {
    setReasons((rs) => (rs.includes(id) ? rs.filter((r) => r !== id) : [...rs, id]));
  };
  return (
    <div data-testid="admin-vendors-clarification-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl border border-slate-200 p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700 mb-1">
              Request clarification
            </div>
            <h3 className="font-display text-lg font-bold text-slate-900">Select reasons</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2">
          {CLARIFICATION_REASONS.map((r) => {
            const checked = reasons.includes(r.id);
            return (
              <label key={r.id} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(r.id)}
                  data-testid={`admin-vendors-reason-${r.id}`}
                  className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-slate-700">{r.label}</span>
              </label>
            );
          })}
        </div>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          data-testid="admin-vendors-clarification-note"
          placeholder="Optional note for the vendor…"
          className="mt-4 w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y"
        />
        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-4 py-2">
            Cancel
          </button>
          <button
            onClick={() => onSubmit(reasons, note || null)}
            disabled={reasons.length === 0}
            data-testid="admin-vendors-submit-clarification"
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <FileText className="w-3.5 h-3.5" /> Send request
          </button>
        </div>
      </div>
    </div>
  );
}
